import i18n from 'simple-react-i18n'
import { isNil, mapValues, round } from 'lodash'
import { STATION_TYPE_CONSTANT } from '../../station/constants/StationConstants'
import { formatMilliers } from 'utils/StringUtil'

export const getStationLabel = ({ code, name, siteCode }) => `${code || siteCode}${name ? `\n${name}` : ''}`

const valueKey = (siteType, siteCode, column) => `${siteType}_${siteCode}_${column}`

const sumValues = (stations, valuesByKey, column) => stations.reduce((acc, station) => {
    const value = valuesByKey[valueKey(station.siteType, station.siteCode, column)]
    return isNil(value) ? acc : acc + value
}, 0)

// Lignes du bilan de volumes d'une unité, dans l'ordre d'affichage : les stations d'entrée, les
// sommes eau souterraine / eau superficielle, la somme en entrée, puis les compteurs de sortie et
// leurs totaux. Neutre vis-à-vis du rendu : le tableau et l'export s'appuient tous deux dessus.
export const getVolumesRows = ({ stationsIn = [], stationsOut = [], measures = [], columns = [], formatDate = () => '' }) => {
    const valuesByKey = mapValues(measures.reduce((acc, { siteType, siteCode, date, value }) => {
        const key = valueKey(siteType, siteCode, formatDate(date))
        return { ...acc, [key]: (acc[key] || 0) + value }
    }, {}), value => round(value, 2) || 0)

    const stationRow = station => ({
        label: getStationLabel(station),
        station,
        values: columns.reduce((acc, column) => {
            const value = valuesByKey[valueKey(station.siteType, station.siteCode, column)]
            return { ...acc, [column]: isNil(value) ? '' : formatMilliers(value, true) }
        }, {}),
    })

    const totalRow = (label, getValue) => ({
        label,
        isTotal: true,
        values: columns.reduce((acc, column) => ({ ...acc, [column]: getValue(column) || '' }), {}),
    })

    const piezos = stationsIn.filter(s => s.siteType === STATION_TYPE_CONSTANT.piezometry)
    const hydros = stationsIn.filter(s => s.siteType === STATION_TYPE_CONSTANT.hydrometry)

    const groundWaterSum = column => round(sumValues(piezos, valuesByKey, column), 2)
    const surfaceWaterSum = column => round(sumValues(hydros, valuesByKey, column), 2)
    const entrySum = column => round(sumValues(stationsIn, valuesByKey, column), 2)
    const outSum = column => round(sumValues(stationsOut, valuesByKey, column), 2)

    return [
        ...stationsIn.map(stationRow),
        ...(piezos.length ? [totalRow(i18n.groundWaterSum, groundWaterSum)] : []),
        ...(hydros.length ? [totalRow(i18n.surfaceWaterSum, surfaceWaterSum)] : []),
        totalRow(i18n.entrySum, entrySum),
        ...stationsOut.map(stationRow),
        ...(stationsOut.length ? [
            totalRow(i18n.outSum, outSum),
            totalRow(i18n.difference, column => round(entrySum(column) - outSum(column), 2)),
        ] : []),
    ]
}

// Un tableau exportable : la première ligne porte les en-têtes, et le titre de l'unité quand
// plusieurs tableaux sont concaténés dans un même fichier.
export const toVolumesExportRows = (rows, columns, tableTitle) => rows.map((row, index) => ({
    station: row.label,
    ...row.values,
    headers: index === 0 ? ['station', ...columns] : [],
    ...(index === 0 && tableTitle ? { tableTitle } : {}),
}))

export const getProductionUnitLabel = productionUnit => `${productionUnit.code || productionUnit.id} - ${productionUnit.name || ''}`
